/**
 * Eligibility Calculator Service
 *
 * Calculates employee benefits eligibility dates based on:
 * - Hire date
 * - Waiting period (days or months)
 * - Entry timing (immediate, first of month, etc.)
 * - Employment type and hours requirements
 *
 * Validates against ACA 90-day maximum waiting period.
 */

// ============================================================================
// Types
// ============================================================================

export type WaitingPeriodType = "days" | "months";

export type EntryDateType =
  | "immediate"
  | "first_of_month"
  | "first_of_following_month"
  | "15th_of_month";

export interface EligibilityInput {
  /** Employee's hire date */
  hireDate: Date | string;
  /** Waiting period duration (in days or months based on waitingPeriodType) */
  waitingPeriod: number;
  /** Unit for waiting period */
  waitingPeriodType: WaitingPeriodType;
  /** When coverage can start after eligibility */
  entryDate: EntryDateType;
  /** Employee's employment type (e.g., "full-time", "part-time") */
  employmentType: string;
  /** Employee's scheduled hours per week */
  scheduledHours?: number;
  /** Employment types allowed by the plan */
  planEmploymentTypes: string[];
  /** Minimum hours per week required (default: 30 per ACA) */
  minHoursPerWeek?: number;
  /** Maximum waiting period in days per ACA (default: 90) */
  acaMaxWaitingDays?: number;
  /** Days before effective date that enrollment must be completed (default: 7) */
  enrollmentBufferDays?: number;
}

export interface EligibilityResult {
  /** Whether the employee is eligible for benefits */
  isEligible: boolean;
  /** Date when employee becomes eligible to enroll (hire + waiting period) */
  eligibilityDate: Date | null;
  /** Date when coverage actually starts (after entry timing applied) */
  effectiveDate: Date | null;
  /** Deadline for completing enrollment */
  enrollmentDueDate: Date | null;
  /** Days from today until eligibility date */
  daysUntilEligible: number;
  /** Days from today until effective date */
  daysUntilEffective: number;
  /** Days from today until enrollment due date */
  daysUntilDue: number;
  /** Reason for ineligibility (if not eligible) */
  ineligibilityReason?: string;
  /** Whether the configuration complies with ACA 90-day maximum */
  acaCompliant: boolean;
  /** Warning message if approaching or exceeding ACA limit */
  acaWarning?: string;
  /** Total days from hire to effective date */
  totalWaitingDays: number;
}

// ============================================================================
// Constants
// ============================================================================

/** ACA requires full-time employees work at least 30 hours per week */
export const ACA_FULL_TIME_HOURS = 30;

/** ACA maximum waiting period is 90 days */
export const ACA_MAX_WAITING_DAYS = 90;

/** Default days before effective date for enrollment deadline */
export const DEFAULT_ENROLLMENT_BUFFER_DAYS = 7;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse a date from various formats to a Date object
 */
export function parseDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) {
    // Normalize to UTC noon to prevent timezone shifts in date arithmetic
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0, 0));
  }
  // Date-only strings ("YYYY-MM-DD") are parsed as UTC midnight by JS spec.
  // Parse explicitly as UTC noon to avoid off-by-one from timezone shifts.
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-").map(Number);
    const parsed = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

/**
 * Add months to a date
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setUTCMonth(result.getUTCMonth() + months);
  return result;
}

/**
 * Get the first day of the month for a given date
 */
export function getFirstOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0, 0));
}

/**
 * Get the first day of the next month for a given date
 */
export function getFirstOfNextMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 12, 0, 0, 0));
}

/**
 * Get the first day of the month after the month of the given date
 * (i.e., the 1st of the next month, regardless of the day of the given date)
 *
 * Example: March 20 → April 1 (not May 1)
 *
 * Note: This differs from getFirstOfNextMonth only in semantic intent.
 * getFirstOfNextMonth is used for "first_of_month" entry timing (where
 * coverage starts on the 1st if eligibility is exactly on the 1st).
 * This function is used for "first_of_following_month" where coverage
 * ALWAYS starts on the 1st of the next month.
 */
export function getFirstOfFollowingMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 12, 0, 0, 0));
}

/**
 * Get the 15th of the current or next month based on the date
 * If date is on or before the 15th, return 15th of current month
 * If date is after the 15th, return 15th of next month
 */
export function get15thOfMonth(date: Date): Date {
  const day = date.getUTCDate();
  if (day <= 15) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 15, 12, 0, 0, 0));
  }
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 15, 12, 0, 0, 0));
}

/**
 * Calculate days between two dates
 */
export function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
}

/**
 * Calculate days from today until a target date
 * Returns negative if date is in the past
 */
export function daysFromNow(targetDate: Date | null): number {
  if (!targetDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  return daysBetween(today, target);
}

/**
 * Normalize employment type string for comparison
 */
export function normalizeEmploymentType(type: string): string {
  return type.toLowerCase().replace(/[_\s-]+/g, "-").trim();
}

/**
 * Check if an employment type matches any in the allowed list
 */
export function isEmploymentTypeAllowed(
  employeeType: string,
  allowedTypes: string[]
): boolean {
  const normalizedEmployee = normalizeEmploymentType(employeeType);
  return allowedTypes.some(
    (allowed) => normalizeEmploymentType(allowed) === normalizedEmployee
  );
}

// ============================================================================
// Main Calculation Function
// ============================================================================

/**
 * Calculate eligibility dates for an employee based on plan settings
 *
 * @param input - Eligibility calculation input parameters
 * @returns Calculated eligibility result with dates and compliance info
 *
 * @example
 * ```typescript
 * const result = calculateEligibility({
 *   hireDate: new Date('2026-01-05'),
 *   waitingPeriod: 30,
 *   waitingPeriodType: 'days',
 *   entryDate: 'first_of_month',
 *   employmentType: 'full-time',
 *   scheduledHours: 40,
 *   planEmploymentTypes: ['full-time', 'variable-hour'],
 * });
 *
 * // Result:
 * // {
 * //   isEligible: true,
 * //   eligibilityDate: 2026-02-04, // hire + 30 days
 * //   effectiveDate: 2026-03-01,   // first of month after eligibility
 * //   enrollmentDueDate: 2026-02-22, // 7 days before effective
 * //   acaCompliant: true,
 * //   totalWaitingDays: 55
 * // }
 * ```
 */
export function calculateEligibility(input: EligibilityInput): EligibilityResult {
  const {
    hireDate: hireDateInput,
    waitingPeriod,
    waitingPeriodType,
    entryDate,
    employmentType,
    scheduledHours,
    planEmploymentTypes,
    minHoursPerWeek = ACA_FULL_TIME_HOURS,
    acaMaxWaitingDays = ACA_MAX_WAITING_DAYS,
    enrollmentBufferDays = DEFAULT_ENROLLMENT_BUFFER_DAYS,
  } = input;

  // Parse hire date
  const hireDate = parseDate(hireDateInput);
  if (!hireDate) {
    return {
      isEligible: false,
      eligibilityDate: null,
      effectiveDate: null,
      enrollmentDueDate: null,
      daysUntilEligible: 0,
      daysUntilEffective: 0,
      daysUntilDue: 0,
      ineligibilityReason: "Invalid or missing hire date",
      acaCompliant: true,
      totalWaitingDays: 0,
    };
  }

  // Check 1: Employment type eligibility
  if (!isEmploymentTypeAllowed(employmentType, planEmploymentTypes)) {
    return {
      isEligible: false,
      eligibilityDate: null,
      effectiveDate: null,
      enrollmentDueDate: null,
      daysUntilEligible: 0,
      daysUntilEffective: 0,
      daysUntilDue: 0,
      ineligibilityReason: `Employment type "${employmentType}" is not eligible for this plan. Eligible types: ${planEmploymentTypes.join(", ")}`,
      acaCompliant: true,
      totalWaitingDays: 0,
    };
  }

  // Check 2: Hours requirement (for variable hour or when explicitly set)
  if (scheduledHours !== undefined && scheduledHours < minHoursPerWeek) {
    // Part-time employees may still be eligible if part-time is in the allowed types
    const isPartTimeAllowed = isEmploymentTypeAllowed("part-time", planEmploymentTypes);
    if (!isPartTimeAllowed) {
      return {
        isEligible: false,
        eligibilityDate: null,
        effectiveDate: null,
        enrollmentDueDate: null,
        daysUntilEligible: 0,
        daysUntilEffective: 0,
        daysUntilDue: 0,
        ineligibilityReason: `Scheduled hours (${scheduledHours}/week) is below the minimum requirement of ${minHoursPerWeek} hours/week`,
        acaCompliant: true,
        totalWaitingDays: 0,
      };
    }
  }

  // Calculate eligibility date (hire + waiting period)
  let eligibilityDate: Date;
  if (waitingPeriodType === "months") {
    eligibilityDate = addMonths(hireDate, waitingPeriod);
  } else {
    eligibilityDate = addDays(hireDate, waitingPeriod);
  }

  // Calculate effective date based on entry timing
  // Normalize entryDate to underscores — DB values may contain dashes (e.g. "first-of-following-month")
  const normalizedEntryDate = String(entryDate || "first_of_following_month").replace(/-/g, "_");

  let effectiveDate: Date;
  switch (normalizedEntryDate) {
    case "immediate":
      effectiveDate = new Date(eligibilityDate);
      break;
    case "first_of_month":
      // If eligibility date is on the 1st, use that month; otherwise next month
      if (eligibilityDate.getUTCDate() === 1) {
        effectiveDate = new Date(eligibilityDate);
      } else {
        effectiveDate = getFirstOfNextMonth(eligibilityDate);
      }
      break;
    case "first_of_following_month":
      // Always the 1st of the month AFTER the eligibility month
      effectiveDate = getFirstOfFollowingMonth(eligibilityDate);
      break;
    case "15th_of_month":
      effectiveDate = get15thOfMonth(eligibilityDate);
      break;
    default:
      // Unrecognized value — fall back to first of following month (safest default)
      effectiveDate = getFirstOfFollowingMonth(eligibilityDate);
  }

  // Calculate enrollment due date (effective - buffer days)
  // But enrollment due can't be before eligibility date (can't enroll before eligible)
  const rawEnrollmentDueDate = addDays(effectiveDate, -enrollmentBufferDays);
  const enrollmentDueDate = rawEnrollmentDueDate.getTime() > eligibilityDate.getTime()
    ? rawEnrollmentDueDate
    : new Date(eligibilityDate);

  // Calculate total waiting days (hire to effective)
  const totalWaitingDays = daysBetween(hireDate, effectiveDate);

  // ACA compliance check
  const acaCompliant = totalWaitingDays <= acaMaxWaitingDays;
  let acaWarning: string | undefined;
  if (!acaCompliant) {
    acaWarning = `Total waiting period of ${totalWaitingDays} days exceeds ACA maximum of ${acaMaxWaitingDays} days`;
  } else if (totalWaitingDays > acaMaxWaitingDays - 15) {
    // Warn if within 15 days of limit
    acaWarning = `Total waiting period of ${totalWaitingDays} days is approaching ACA maximum of ${acaMaxWaitingDays} days`;
  }

  return {
    isEligible: true,
    eligibilityDate,
    effectiveDate,
    enrollmentDueDate,
    daysUntilEligible: daysFromNow(eligibilityDate),
    daysUntilEffective: daysFromNow(effectiveDate),
    daysUntilDue: daysFromNow(enrollmentDueDate),
    acaCompliant,
    acaWarning,
    totalWaitingDays,
  };
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate plan eligibility settings against ACA requirements
 * Used when configuring a plan to warn about invalid configurations
 */
export function validatePlanEligibilitySettings(settings: {
  waitingPeriod: number;
  waitingPeriodType: WaitingPeriodType;
  entryDate: EntryDateType;
}): { valid: boolean; warning?: string } {
  const { waitingPeriod, waitingPeriodType, entryDate } = settings;

  // Convert waiting period to approximate days
  let waitingDays = waitingPeriod;
  if (waitingPeriodType === "months") {
    waitingDays = waitingPeriod * 30; // Approximate
  }

  // Estimate additional days based on entry timing (worst case)
  // These values represent the maximum additional days that could be added
  // between eligibility date and effective date based on the entry timing rule.
  let entryTimingDays = 0;
  switch (entryDate) {
    case "immediate":
      // Coverage starts immediately on eligibility date
      entryTimingDays = 0;
      break;
    case "first_of_month":
      // Coverage starts on the 1st of same month (if eligibility is on 1st) or next month
      // Worst case: eligibility on day 2 → wait ~30 days for next month's 1st
      entryTimingDays = 30;
      break;
    case "first_of_following_month":
      // Coverage ALWAYS starts on the 1st of the next month
      // Worst case: eligibility on day 1 → wait entire month (~31 days) for next month's 1st
      entryTimingDays = 31;
      break;
    case "15th_of_month":
      // Coverage starts on the 15th of current (if before 15th) or next month
      // Worst case: eligibility on day 16 → wait ~29 days for next month's 15th
      entryTimingDays = 29;
      break;
  }

  const maxPossibleDays = waitingDays + entryTimingDays;

  if (maxPossibleDays > ACA_MAX_WAITING_DAYS) {
    return {
      valid: false,
      warning: `This configuration could result in a waiting period of up to ${maxPossibleDays} days, which may exceed the ACA maximum of ${ACA_MAX_WAITING_DAYS} days depending on hire date.`,
    };
  }

  if (maxPossibleDays > ACA_MAX_WAITING_DAYS - 15) {
    return {
      valid: true,
      warning: `This configuration could result in a waiting period close to the ACA maximum of ${ACA_MAX_WAITING_DAYS} days for some hire dates.`,
    };
  }

  return { valid: true };
}

// ============================================================================
// Batch Calculation
// ============================================================================

export interface EmployeeEligibilityInput extends EligibilityInput {
  employeeId: number;
  employeeName?: string;
}

export interface EmployeeEligibilityResult extends EligibilityResult {
  employeeId: number;
  employeeName?: string;
}

/**
 * Calculate eligibility for multiple employees
 */
export function calculateBulkEligibility(
  employees: EmployeeEligibilityInput[]
): EmployeeEligibilityResult[] {
  return employees.map((employee) => ({
    ...calculateEligibility(employee),
    employeeId: employee.employeeId,
    employeeName: employee.employeeName,
  }));
}

// ============================================================================
// New Hire Enrollment Window
// ============================================================================

/** Default new hire enrollment window duration in days */
export const DEFAULT_NEW_HIRE_WINDOW_DAYS = 30;

export type NewHireEnrollmentStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "expired"
  | "waived";

export interface NewHireWindowResult {
  /** The start of the enrollment window (hire date) */
  windowStartDate: Date;
  /** The end of the enrollment window (hire date + window days) */
  windowEndDate: Date;
  /** Whether the window is currently open for enrollment */
  isWindowOpen: boolean;
  /** Days remaining until window closes (0 if closed) */
  daysRemaining: number;
  /** Days since window closed (0 if still open) */
  daysPastDeadline: number;
  /** Whether the window has expired */
  isExpired: boolean;
}

/**
 * Calculate the new hire enrollment window for an employee.
 *
 * The new hire enrollment window allows employees to SELECT plans within
 * a configurable number of days from their hire date, regardless of when
 * coverage actually becomes effective (based on waiting period and entry timing).
 *
 * @param hireDate - Employee's hire date
 * @param windowDays - Number of days from hire date to select plans (default: 30)
 * @returns Window information including dates and status
 *
 * @example
 * ```typescript
 * const window = calculateNewHireWindow(new Date('2026-01-05'), 30);
 * // Result:
 * // {
 * //   windowStartDate: 2026-01-05,
 * //   windowEndDate: 2026-02-04,
 * //   isWindowOpen: true (if today is between these dates),
 * //   daysRemaining: 25,
 * //   daysPastDeadline: 0,
 * //   isExpired: false
 * // }
 * ```
 */
export function calculateNewHireWindow(
  hireDate: Date | string,
  windowDays: number = DEFAULT_NEW_HIRE_WINDOW_DAYS
): NewHireWindowResult {
  const parsedHireDate = parseDate(hireDate);

  if (!parsedHireDate) {
    // Return a window that's already expired if no valid hire date
    const now = new Date();
    return {
      windowStartDate: now,
      windowEndDate: now,
      isWindowOpen: false,
      daysRemaining: 0,
      daysPastDeadline: 0,
      isExpired: true,
    };
  }

  const windowStartDate = new Date(parsedHireDate);
  const windowEndDate = addDays(parsedHireDate, windowDays);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  windowStartDate.setHours(0, 0, 0, 0);
  windowEndDate.setHours(0, 0, 0, 0);

  const isAfterStart = today >= windowStartDate;
  const isBeforeEnd = today <= windowEndDate;
  const isWindowOpen = isAfterStart && isBeforeEnd;
  const isExpired = today > windowEndDate;

  const daysRemaining = isWindowOpen ? daysBetween(today, windowEndDate) : 0;
  const daysPastDeadline = isExpired ? daysBetween(windowEndDate, today) : 0;

  return {
    windowStartDate: new Date(parsedHireDate),
    windowEndDate,
    isWindowOpen,
    daysRemaining,
    daysPastDeadline,
    isExpired,
  };
}

/**
 * Extended eligibility result that includes new hire window information
 */
export interface EligibilityWithNewHireWindow extends EligibilityResult {
  /** New hire enrollment window information */
  newHireWindow: NewHireWindowResult;
  /** Whether employee can currently enroll via new hire window */
  canEnrollAsNewHire: boolean;
}

/**
 * Calculate eligibility with new hire window information combined.
 *
 * This provides a complete picture of both:
 * 1. When coverage will be effective (based on waiting period + entry timing)
 * 2. When the employee can SELECT plans (based on new hire window)
 *
 * @param eligibilityInput - Standard eligibility calculation input
 * @param windowDays - New hire window duration (default: 30)
 * @returns Combined eligibility and new hire window result
 */
export function calculateEligibilityWithNewHireWindow(
  eligibilityInput: EligibilityInput,
  windowDays: number = DEFAULT_NEW_HIRE_WINDOW_DAYS
): EligibilityWithNewHireWindow {
  const eligibility = calculateEligibility(eligibilityInput);
  const newHireWindow = calculateNewHireWindow(eligibilityInput.hireDate, windowDays);

  // Can enroll as new hire if:
  // 1. Window is open
  // 2. Employee is eligible for the plan (based on employment type, hours, etc.)
  const canEnrollAsNewHire = newHireWindow.isWindowOpen && eligibility.isEligible;

  return {
    ...eligibility,
    newHireWindow,
    canEnrollAsNewHire,
  };
}

/**
 * Get new hire enrollment status label and variant for UI display
 */
export function getNewHireEnrollmentStatus(
  windowResult: NewHireWindowResult,
  progressStatus?: NewHireEnrollmentStatus
): {
  status: NewHireEnrollmentStatus;
  label: string;
  variant: "success" | "warning" | "destructive" | "default" | "secondary";
} {
  // If we have a progress status, use it
  if (progressStatus) {
    switch (progressStatus) {
      case "completed":
        return {
          status: "completed",
          label: "Enrollment Complete",
          variant: "success",
        };
      case "waived":
        return {
          status: "waived",
          label: "Coverage Waived",
          variant: "secondary",
        };
      case "expired":
        return {
          status: "expired",
          label: "Window Expired",
          variant: "destructive",
        };
      case "in_progress":
        return {
          status: "in_progress",
          label: windowResult.isWindowOpen
            ? `${windowResult.daysRemaining} days left`
            : "In Progress",
          variant: "warning",
        };
      case "not_started":
      default:
        // Fall through to calculate from window
        break;
    }
  }

  // Calculate status from window
  if (windowResult.isExpired) {
    return {
      status: "expired",
      label: `Expired ${windowResult.daysPastDeadline} days ago`,
      variant: "destructive",
    };
  }

  if (windowResult.isWindowOpen) {
    if (windowResult.daysRemaining <= 3) {
      return {
        status: "not_started",
        label: `${windowResult.daysRemaining} days left - Urgent`,
        variant: "destructive",
      };
    }
    if (windowResult.daysRemaining <= 7) {
      return {
        status: "not_started",
        label: `${windowResult.daysRemaining} days left`,
        variant: "warning",
      };
    }
    return {
      status: "not_started",
      label: `${windowResult.daysRemaining} days remaining`,
      variant: "default",
    };
  }

  return {
    status: "not_started",
    label: "Not Started",
    variant: "default",
  };
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a date for display (YYYY-MM-DD)
 */
export function formatDate(date: Date | null): string {
  if (!date) return "-";
  return date.toISOString().split("T")[0];
}

/**
 * Format days remaining as human-readable string
 */
export function formatDaysRemaining(days: number): string {
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `${days} days`;
}

/**
 * Get eligibility status label
 */
export function getEligibilityStatus(result: EligibilityResult): {
  status: "eligible" | "pending" | "ineligible";
  label: string;
  variant: "success" | "warning" | "destructive" | "default";
} {
  if (!result.isEligible) {
    return {
      status: "ineligible",
      label: "Ineligible",
      variant: "destructive",
    };
  }

  if (result.daysUntilEffective > 0) {
    return {
      status: "pending",
      label: `Eligible in ${formatDaysRemaining(result.daysUntilEligible)}`,
      variant: "warning",
    };
  }

  return {
    status: "eligible",
    label: "Eligible",
    variant: "success",
  };
}
